import { RadioGroup, Slider } from "@ark-ui/react";
import {
  FACILITY_STAFF_TIER,
  TEXT_TO_SPEECH_MODE,
  type TextToSpeechMode,
  TextToSpeechModeSchema,
  isEnabled,
  isFacilityKey,
  isOrganizationKey,
  parse,
  t as tt,
} from "@packages/core";
import { useForm, useSelector } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuPlay } from "react-icons/lu";
import { toast } from "sonner";
import { Merge } from "type-fest";
import * as v from "valibot";
import {
  ButtonStyle2,
  Divider,
  PageName,
  RADIO_GROUP_STYLE,
  SLIDER_STYLE,
  Spinner,
  TypographyStyle,
} from "#components";
import {
  $confirm,
  modifyFacility,
  nativeApplication,
  setNativeApplicationTextToSpeechPitch,
  setNativeApplicationTextToSpeechRate,
  setNativeApplicationTextToSpeechVolume,
  useFacilityQuery,
  useFacilityStaffQuery,
  useNativeApplicationRevisionQuery,
  useOperatorQuery,
  useTextToSpeechRate,
  useTextToSpeechVolume,
  useUsername,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/text-to-speech/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { facilityKey } = Route.useParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const username = useUsername();

  const facilityQuery = useFacilityQuery(facilityKey);
  const { data: facility } = facilityQuery;
  const facilityStaffQuery = useFacilityStaffQuery(facilityKey, username);
  const operatorQuery = useOperatorQuery(username);
  const [textToSpeechRate, setTextToSpeechRate] = useTextToSpeechRate();
  const [textToSpeechVolume, setTextToSpeechVolume] = useTextToSpeechVolume();

  const formSchema = useMemo(
    function () {
      return v.object({
        /** モード */
        mode: TextToSpeechModeSchema(),
        /** ピッチ */
        pitch: v.pipe(
          v.number(),
          v.minValue(0, function (issue) {
            return t("%TooLittle.0%", { $: { minimum: issue.requirement } });
          }),
          v.maxValue(2, function (issue) {
            return t("%TooMuch.0%", { $: { maximum: issue.requirement } });
          }),
        ),
        /** レート */
        rate: v.pipe(
          v.number(),
          v.minValue(0.1, function (issue) {
            return t("%TooLittle.0%", { $: { minimum: issue.requirement } });
          }),
          // JavaScriptの上限は`10`だが、実用的な範囲に制限している。
          v.maxValue(2, function (issue) {
            return t("%TooMuch.0%", { $: { maximum: issue.requirement } });
          }),
        ),
        /** 音量 */
        volume: v.pipe(
          v.number(),
          v.minValue(0, function (issue) {
            return t("%TooLittle.0%", { $: { minimum: issue.requirement } });
          }),
          v.maxValue(1, function (issue) {
            return t("%TooMuch.0%", { $: { maximum: issue.requirement } });
          }),
        ),
      });
    },
    [t],
  );

  const form = useForm({
    defaultValues: useMemo(
      function (): v.InferInput<typeof formSchema> {
        return {
          mode: facility?.textToSpeechMode ?? TEXT_TO_SPEECH_MODE.ROOM_NAME_BED_NAME,
          pitch: 1.0,
          rate: textToSpeechRate,
          volume: textToSpeechVolume,
        };
      },
      [facility, textToSpeechRate, textToSpeechVolume],
    ),
    async onSubmit({ value: input }) {
      let output;
      try {
        output = parse(formSchema, input);
        console.debug("フォームが送信されました。", { input, output });
      } catch (error) {
        console.error("フォームの解析に失敗しました。", { input, output }, error);
        toast.error(t("%FormParsingFailed.0%"));
        return;
      }

      try {
        setTextToSpeechRate(output.rate);
        setTextToSpeechVolume(output.volume);

        if (
          isEnabled(operatorQuery.data) ||
          isOrganizationKey(username) ||
          isFacilityKey(username) ||
          (isEnabled(facilityStaffQuery.data) &&
            facilityStaffQuery.data.tier <= FACILITY_STAFF_TIER.ADMINISTRATOR)
        ) {
          await modifyFacility({ facilityKey, textToSpeechMode: output.mode });
        }

        await setNativeApplicationTextToSpeechPitch(output.pitch);
        await setNativeApplicationTextToSpeechRate(output.rate);
        await setNativeApplicationTextToSpeechVolume(output.volume);
      } catch (error) {
        console.error(error);
        toast.error(error instanceof Error ? error.message : "");
        return;
      }

      toast.success(t("%Saved.0%"));

      await navigate({
        ignoreBlocker: true,
        params: { facilityKey },
        to: "/facilities/$facilityKey",
      });
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: formSchema },
  });

  const formStoreStateIsSubmitting = useSelector(form.store, function (state) {
    return state.isSubmitting;
  });
  const mode = useSelector(form.store, function (state) {
    return state.values.mode;
  });
  const rate = useSelector(form.store, function (state) {
    return state.values.rate;
  });
  const volume = useSelector(form.store, function (state) {
    return state.values.volume;
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: t("%Form.1%"),
          no: t("%Stay.0%"),
          title: t("%Form.0%"),
          yes: t("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <form
      className={css({ padding: { smDown: "16px", sm: "32px 24px" } })}
      data-main
      onSubmit={function (event) {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <PageName>
        {t("%TextToSpeech.0%") +
          " - " +
          (facilityQuery.data?.name ??
            (!facilityQuery.isFetching ? t("%Unknown.0%") : t("%Loading.0%")))}
      </PageName>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "12px",
          flexGrow: 1,
          marginTop: { smDown: "12px", sm: "6px" },
          marginLeft: "auto",
          marginRight: "auto",
          padding: { smDown: "16px", sm: "16px 24px" },
          maxWidth: "480px",
          minHeight: 0,
        })}
      >
        <h1
          className={TypographyStyle({ fontSize: "18px", fontWeight: 600, lineHeight: "14 / 9" })}
        >
          {t("%TextToSpeech.0%")}
        </h1>

        <p
          className={TypographyStyle({
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "10 / 7",
            marginTop: "16px",
          })}
        >
          {t("%TextToSpeech.1%")}
        </p>

        <PreviewPlaybackButton mode={mode} pitch={1.0} rate={rate} type="button" volume={volume} />

        <Divider className={css({ marginTop: "16px" })} />

        {(isEnabled(operatorQuery.data) ||
          isOrganizationKey(username) ||
          isFacilityKey(username) ||
          (isEnabled(facilityStaffQuery.data) &&
            facilityStaffQuery.data.tier <= FACILITY_STAFF_TIER.ADMINISTRATOR)) && (
          <Fragment>
            <h2
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: 1,
                marginTop: "16px",
              })}
            >
              {t("%TextToSpeechMode.0%")}
            </h2>

            <form.Field name="mode">
              {function (field) {
                return (
                  <RadioGroup.Root
                    className={css(RADIO_GROUP_STYLE)}
                    disabled={formStoreStateIsSubmitting}
                    onValueChange={function (event) {
                      field.handleChange(event.value as any);
                    }}
                    value={field.state.value}
                  >
                    <RadioGroup.Indicator />
                    {[
                      TEXT_TO_SPEECH_MODE.ROOM_NAME_BED_NAME,
                      TEXT_TO_SPEECH_MODE.RESIDENT_NAME,
                    ].map(function (textToSpeechMode) {
                      return (
                        <RadioGroup.Item key={textToSpeechMode} value={textToSpeechMode}>
                          <RadioGroup.ItemControl />
                          <RadioGroup.ItemText>
                            {t(`%TextToSpeechMode.${textToSpeechMode}.0%`)}
                          </RadioGroup.ItemText>
                          <RadioGroup.ItemText data-part="item-description">
                            {t(`%TextToSpeechMode.${textToSpeechMode}.1%`)}
                          </RadioGroup.ItemText>
                          <RadioGroup.ItemHiddenInput />
                        </RadioGroup.Item>
                      );
                    })}
                  </RadioGroup.Root>
                );
              }}
            </form.Field>

            <Divider className={css({ marginTop: "16px" })} />
          </Fragment>
        )}

        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1,
            marginTop: "16px",
          })}
        >
          {t("%AdvancedAudioSettings.0%")}
        </h2>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: "8px",
          })}
        >
          <form.Field name="volume">
            {function (field) {
              return (
                <Slider.Root
                  className={css(SLIDER_STYLE)}
                  disabled={formStoreStateIsSubmitting}
                  max={1}
                  min={0}
                  onValueChange={function (details) {
                    const value = details.value.at(0);
                    if (value !== undefined && !Number.isNaN(value)) {
                      field.handleChange(value);
                    }
                  }}
                  step={0.01}
                  value={[field.state.value]}
                >
                  <Slider.Label>{t("%Volume.0%")}</Slider.Label>
                  <Slider.ValueText>{Math.round(field.state.value * 100)}%</Slider.ValueText>
                  <Slider.Control>
                    <Slider.Track>
                      <Slider.Range />
                    </Slider.Track>
                    <Slider.Thumb index={0}>
                      <Slider.HiddenInput />
                    </Slider.Thumb>
                  </Slider.Control>
                </Slider.Root>
              );
            }}
          </form.Field>

          <form.Field name="rate">
            {function (field) {
              return (
                <Slider.Root
                  className={css(SLIDER_STYLE)}
                  disabled={formStoreStateIsSubmitting}
                  max={2}
                  min={0.1}
                  onValueChange={function (details) {
                    const value = details.value.at(0);
                    if (value !== undefined && !Number.isNaN(value)) {
                      field.handleChange(value);
                    }
                  }}
                  step={0.1}
                  value={[field.state.value]}
                >
                  <Slider.Label>{t("%SpeechRate.0%")}</Slider.Label>
                  <Slider.ValueText>{field.state.value.toFixed(1)}x</Slider.ValueText>
                  <Slider.Control>
                    <Slider.Track>
                      <Slider.Range />
                    </Slider.Track>
                    <Slider.Thumb index={0}>
                      <Slider.HiddenInput />
                    </Slider.Thumb>
                  </Slider.Control>
                </Slider.Root>
              );
            }}
          </form.Field>
        </div>

        <div
          className={css({
            columnGap: "8px",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            marginTop: "16px",
          })}
        >
          <Link
            className={ButtonStyle2({ color: "white" })}
            disabled={formStoreStateIsSubmitting}
            onClick={function (event) {
              if (formStoreStateIsSubmitting) {
                event.preventDefault();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey"
          >
            {t("%Cancel.0%")}
          </Link>
          <button
            className={ButtonStyle2({ color: "black" })}
            disabled={formStoreStateIsSubmitting}
            type="submit"
          >
            {formStoreStateIsSubmitting && (
              <Spinner color="#FAFAFA" data-left size="14px" type="a" />
            )}
            {t("%Save.0%")}
          </button>
        </div>
      </div>
    </form>
  );
}

function PreviewPlaybackButton({
  className,
  disabled,
  mode,
  onClick,
  pitch,
  rate,
  volume,
  ...props
}: Merge<
  React.ComponentProps<"button">,
  {
    children?: never;
    mode: TextToSpeechMode;
    pitch: number;
    rate: number;
    volume: number;
  }
>) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const nativeApplicationRevisionQuery = useNativeApplicationRevisionQuery();

  return (
    <button
      {...props}
      className={cx(ButtonStyle2({ color: "gray", marginTop: "8px", width: "stretch" }), className)}
      disabled={disabled || playing}
      onClick={async function (event) {
        onClick?.(event);

        if (playing) {
          return;
        }

        setPlaying(true);

        let text;
        switch (mode) {
          case TEXT_TO_SPEECH_MODE.ROOM_NAME_BED_NAME:
            text = "いちまるいち号室のA";
            break;
          case TEXT_TO_SPEECH_MODE.RESIDENT_NAME:
            text = "山田太郎さん";
            break;
          default:
            text = "";
            break;
        }

        try {
          if (
            window.Android ||
            (window.webkit?.messageHandlers?.iOS &&
              nativeApplicationRevisionQuery.data !== undefined &&
              nativeApplicationRevisionQuery.data >= 6)
          ) {
            await nativeApplication.speak(text, { pitch, rate, volume });
          } else {
            const { promise, resolve, reject } = Promise.withResolvers<void>();
            const speechSynthesisUtterance = new SpeechSynthesisUtterance(text);
            speechSynthesisUtterance.onend = function () {
              resolve();
            };
            speechSynthesisUtterance.onerror = function (event) {
              reject(event.error);
            };
            speechSynthesisUtterance.lang = "ja-JP";
            speechSynthesisUtterance.pitch = pitch;
            speechSynthesisUtterance.rate = rate;
            speechSynthesisUtterance.volume = volume;
            window.speechSynthesis.speak(speechSynthesisUtterance);
            await promise;
          }
        } catch (error) {
          console.error("読み上げ音声の再生に失敗しました。", error);
          toast.error(tt({ ja: "読み上げ音声の再生に失敗しました。" }));
        }

        setPlaying(false);
      }}
    >
      {playing ? (
        <Spinner color="#18181B" data-left size="14px" type="a" />
      ) : (
        <LuPlay data-icon data-left />
      )}
      {t("%PreviewPlayback.0%")}
    </button>
  );
}
